/**
 * Cross-tab mutual exclusion, via the Web Locks API.
 *
 * Two tabs opened on a first visit will both find no saved player, both create
 * one, and both call POST /api/player - leaving two server players, one of them
 * orphaned. Worse, the second registration overwrites the stored recovery code
 * while local state may still hold the first player's id, so the code the player
 * writes down can point at an empty save. That is data loss in the one feature
 * whose entire job is preventing it.
 *
 * Web Locks are scoped to the origin and shared across tabs, which is exactly
 * the scope of the problem.
 */

type LockManagerLike = {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
};

function lockManager(): LockManagerLike | null {
  const candidate = (navigator as Navigator & { locks?: LockManagerLike }).locks;
  return candidate && typeof candidate.request === 'function' ? candidate : null;
}

/**
 * Runs `fn` holding an origin-wide lock.
 *
 * Where Web Locks are unavailable (Safari before 15.4) this degrades to running
 * `fn` directly: single-tab use, which is the overwhelmingly common case, is
 * unaffected, and the multi-tab race returns rather than the app breaking.
 */
export async function withLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  const locks = lockManager();
  if (!locks) return fn();

  try {
    return await locks.request(name, fn);
  } catch (error) {
    // A lock failure must never stop someone playing.
    if (error instanceof Error && error.name === 'NotSupportedError') return fn();
    throw error;
  }
}

export const LOCKS = {
  /** Guards create-if-absent of the local player state. */
  bootstrap: 'rego-bingo:bootstrap',
  /** Guards registration with the server, which mints the recovery code. */
  register: 'rego-bingo:register',
} as const;
